import * as IkeModel from '@ikegps/core-shared';
import { CollectionModelFieldNumberSagUtil, IkeUnit, MultiPoint } from '@ikegps/core-shared';
import { Input } from '../../../components/input/input';
import { NumberInputState } from '../../../components/input/number.input.state';
import { T } from '../../../i18n';
import { m, type MithrilVNode } from '../../../mithril';
import { Services } from '../../../services';
import { CurrentState } from '../../../state/page.state';
import { FieldInfo, type FieldStat } from '../../field.info/field.info';
import { CollectionEditCtrl } from '../collection.view.ctrl';
import type { CollectionFieldView } from './field.view';

function getValue(field: IkeModel.CollectionModelFieldNumber, inputState: NumberInputState): number {
	const value = field.value;

	if (!field.hasValue()) {
		return null;
	}

	if (field.isManual() || field.isSag()) {
		return value;
	}

	if (field.isBearing() || field.isBackBearing()) {
		const angle = IkeUnit.convertAngle(value, IkeUnit.DEGREE, IkeUnit.RADIAN);
		return IkeUnit.round(angle, inputState.precision);
	}

	return null;
}

export const NumberFieldView: CollectionFieldView<IkeModel.CollectionModelFieldNumber> = {
	viewInline(field: IkeModel.CollectionModelFieldNumber): string {
		if (field.isSag()) {
			return IkeUnit.display(field.value, Services.UserSettings.units);
		}

		return field.displayValue;
	},

	hasDetail(field: IkeModel.CollectionModelFieldNumber) {
		return !field.isManual() && field.getCaptures().length > 0;
	},

	viewDetail(field: IkeModel.CollectionModelFieldNumber): MithrilVNode {
		if (!NumberFieldView.hasDetail(field)) {
			return null;
		}

		const collection = field.collection;

		if (field.isSag()) {
			const sagCaptures = CollectionModelFieldNumberSagUtil.getSagCaptures(field);
			const p2pMeasurement = MultiPoint.Points([sagCaptures.start, sagCaptures.end]);
			const stats: FieldStat[] = [
				{ name: T.DISTANCE_SAG, value: field.value },
				{ name: T.DISTANCE_VECTOR, value: FieldInfo.viewVectorInline(p2pMeasurement) },
				{ name: T.DISTANCE_VERTICAL, value: Math.abs(p2pMeasurement.vertical) },
				{ name: T.DISTANCE_HORIZONTAL, value: p2pMeasurement.horizontal },
			];

			return FieldInfo.viewCaptures(collection, [sagCaptures.start, ...sagCaptures.points, sagCaptures.end], stats);
		}

		const captures = field.getCaptures();
		return FieldInfo.viewCaptures(collection, captures);
	},

	viewEdit(field: IkeModel.CollectionModelFieldNumber, ctrl: CollectionEditCtrl): MithrilVNode | string {
		if (field.isSag()) {
			return NumberFieldView.viewInline(field, ctrl);
		}

		let inputState = ctrl.changeState.getState(field.id) as NumberInputState;
		if (inputState == null) {
			inputState = new NumberInputState(field.schema.displayName);
			inputState.onValue(() => {
				field.Mutator.manual(inputState.value);
			});
			inputState.setValue(getValue(field, inputState));
			ctrl.changeState.addState(field.id, inputState);
		}

		const onmouseover = () => {
			CurrentState.setHoverId(field.id);
		};
		const onmouseout = () => {
			CurrentState.setHoverId(null);
		};
		const className = Input.CSS.container;
		return m('div', { className, onmouseover, onmouseout, onclick }, [Input.view(inputState)]);
	},

	hasClear(field: IkeModel.CollectionModelFieldNumber) {
		return field.provider === IkeModel.CollectionModelFieldNumber.PROVIDER_SAG;
	},
};
